import { useTu } from "@src/appV2/i18n/useTu";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, TextField } from "@mui/material";
import { type TuFunction } from "@src/appV2/i18n/types";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { LoadingButton } from "../../../components/LoadingButton";
import { Text } from "../../../components/Text";
import { type ControlType, CONTROL_TYPES } from "../../types";

function getSpendingLimitIncreaseRequestFormSchema(tu: TuFunction, isVariableCharge: boolean) {
  return z.object({
    amount: z
      .number({ invalid_type_error: tu("Please enter a valid number") })
      .positive(
        isVariableCharge ? tu("Amount must be greater than 0") : tu("Hours must be greater than 0")
      ),
  });
}

type SpendingLimitIncreaseRequestFormValues = z.infer<
  ReturnType<typeof getSpendingLimitIncreaseRequestFormSchema>
>;

interface SpendingLimitIncreaseRequestFormProps {
  /** Receives the entered amount in display units: hours for `total`, dollars for `variableCharge`. */
  onSubmit: (amount: number) => void;
  isSubmitting?: boolean;
  controlType?: ControlType;
  defaultAmount?: number;
  /** Sets the `<form>` id so a submit button outside the form (e.g. a dialog footer) can target it. */
  formId?: string;
  hideSubmitButton?: boolean;
}

export function SpendingLimitIncreaseRequestForm(props: SpendingLimitIncreaseRequestFormProps) {
  const {
    onSubmit,
    isSubmitting = false,
    controlType = CONTROL_TYPES.total,
    defaultAmount,
    formId,
    hideSubmitButton = false,
  } = props;
  const { tu } = useTu("spendingControls");

  const isVariableCharge = controlType === CONTROL_TYPES.variableCharge;
  const spendingLimitIncreaseRequestFormSchema = useMemo(
    () => getSpendingLimitIncreaseRequestFormSchema(tu, isVariableCharge),
    [tu, isVariableCharge]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SpendingLimitIncreaseRequestFormValues>({
    resolver: zodResolver(spendingLimitIncreaseRequestFormSchema),
    defaultValues: {
      amount: defaultAmount,
    },
  });

  return (
    <form
      id={formId}
      onSubmit={handleSubmit((values) => {
        onSubmit(values.amount);
      })}
    >
      <Stack spacing={4}>
        <Box>
          <Text
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(1),
            })}
          >
            {isVariableCharge ? tu("Additional weekly boost spend ($)") : tu("Additional hours")}
          </Text>
          <TextField
            {...register("amount", { valueAsNumber: true })}
            fullWidth
            placeholder={tu("Enter value...")}
            type="number"
            error={Boolean(errors.amount)}
            helperText={errors.amount?.message}
          />
        </Box>

        {!hideSubmitButton && (
          <Box>
            <LoadingButton isLoading={isSubmitting} type="submit" variant="primary">
              {tu("Request approval")}
            </LoadingButton>
          </Box>
        )}
      </Stack>
    </form>
  );
}
